/**
 * Public assessment calls — no Innu token, no `X-Innu-App`, no auth context:
 * the candidate opens `/assess/:token` from a text on their phone. Plain
 * `fetch` against the worker (same-origin proxy in production), envelope
 * unwrapped by hand because `ApiClient` needs `AuthProvider`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, ApiError } from '@/lib/api-client';

export type InviteStatus = 'sent' | 'opened' | 'completed';

export interface Invite {
  first_name: string;
  role_title: string;
  status: InviteStatus;
}

/** What the placeholder page records until the real instrument ships. */
export interface PlaceholderResult {
  placeholder: true;
}

export const inviteKey = (token: string) => ['assess', token] as const;

async function unwrap<T>(res: Response, path: string): Promise<T> {
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  const env = parsed as { ok?: boolean; data?: T; error?: { code?: string; message?: string } } | null;
  if (!res.ok || env?.ok === false) {
    throw new ApiError(res.status, parsed, env?.error?.message ?? `Request failed (${res.status}) — ${path}`, env?.error?.code);
  }
  return (env && 'data' in env ? env.data : parsed) as T;
}

export async function fetchInvite(token: string): Promise<Invite> {
  const path = `/api/public/assess/${encodeURIComponent(token)}`;
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: { Accept: 'application/json' } });
  return unwrap<Invite>(res, path);
}

export async function completeInvite(token: string, result: PlaceholderResult): Promise<unknown> {
  const path = `/api/public/assess/${encodeURIComponent(token)}/complete`;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ result }),
  });
  return unwrap<unknown>(res, path);
}

/** `true` when the link is unknown or expired (worker 404 / 410). */
export function isInvalidInvite(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 410);
}

export function useInvite(token: string | undefined) {
  return useQuery({
    queryKey: inviteKey(token ?? ''),
    queryFn: () => fetchInvite(token ?? ''),
    enabled: !!token,
    retry: (count, err) => !isInvalidInvite(err) && count < 1,
    staleTime: Infinity,
  });
}

export function useCompleteInvite(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (result: PlaceholderResult) => completeInvite(token ?? '', result),
    onSuccess: () => {
      qc.setQueryData<Invite>(inviteKey(token ?? ''), (prev) => (prev ? { ...prev, status: 'completed' } : prev));
    },
  });
}
